import { navigate } from 'gatsby-link';
import { logStatsigEvent } from 'atoms/AbTesting/statsigClient';
import { getLocalStorage } from './localStorageHandler';
import { getLanguageFromPath } from 'src/utils/i18n-config';

const getCookies = () => {
  return document.cookie.split('; ').reduce(
    (p, c) => {
      const [key, val] = c.split(/=(.*)/s);
      p[key] = val;

      return p;
    },
    {} as { [key: string]: string }
  );
};

// Glitchy affiliate tracking params: ?sub1={affiliate_id}&sub2={offer_id}&sub3={transaction_id}&sub4={source}
const GLITCHY_PARAMS = ['sub1', 'sub2', 'sub3', 'sub4'];

const isGlitchyParam = (param: string): boolean => GLITCHY_PARAMS.includes(param);

const filterQueryParams = (): string => {
  const searchString = location?.search;
  if (!searchString) {
    return '';
  }

  const applicableQueries = searchString
    .replace('?', '')
    .split('&')
    .filter((param) => {
      // We're only interested in utm queries deriving from adds
      const candidate = param.split('=')[0];
      return (
        candidate.includes('utm') ||
        candidate.includes('msclkid') ||
        candidate.includes('li_fat_id') ||
        candidate.includes('fbclid') ||
        candidate.includes('gclid') ||
        isGlitchyParam(candidate)
      );
    })
    .join('&');

  if (applicableQueries.length) {
    return `${applicableQueries}`;
  } else {
    return '';
  }
};

export const propagateUtmParams = (): string => {
  if (typeof window === 'undefined') {
    return '';
  }
  // Explicitely using "window" to avoid "not found" declaration
  const currentQueryPass = window.sessionStorage.getItem('query_pass');
  if (currentQueryPass) {
    const queryPass = filterQueryParams();
    // Is it the same values? Else repalce
    if (queryPass && queryPass !== currentQueryPass) {
      window.sessionStorage.setItem('query_pass', queryPass);
      return queryPass.replace('?', '');
    }
    return currentQueryPass.replace('?', '');
  } else {
    const queryPass = filterQueryParams();
    if (queryPass) {
      window.sessionStorage.setItem('query_pass', queryPass);
      return queryPass;
    } else {
      return '';
    }
  }
};

const propagateGaParams = (valueOnly: boolean): string => {
  let { _gid, _ga } = getCookies() as { _gid: string; _ga: string };
  if (!_gid || !_ga) {
    // _gid has not been set
    (window as any).TrackJS && (window as any).TrackJS.addMetadata('analytics_status', 'missing');
    return '';
  }
  (window as any).TrackJS && (window as any).TrackJS.addMetadata('analytics_status', 'existing');

  // >> Make sure _ga is cleared of version tagging
  _gid = _gid.split('.').slice(2).join('.');
  _ga = _ga.split('.').slice(2).join('.');
  const timeSequenceAsStrings = (a: string) => {
    let b = 1,
      c;
    if (a)
      for (b = 0, c = a.length - 1; 0 <= c; c--) {
        let d = a.charCodeAt(c);
        b = ((b << 6) & 268435455) + d + (d << 14);
        d = b & 266338304;
        b = 0 != d ? b ^ (d >> 21) : b;
      }
    return b;
  };

  const parseTimeStamp = (a: string, b: number) => {
    const c = new Date(),
      NAVIGATOR = window.navigator,
      e = c.getHours() + Math.floor((c.getMinutes() + b) / 60);
    // (c.getFullYear() - 1900) replaces deprecated c.getYear() API
    return timeSequenceAsStrings(
      [
        a,
        NAVIGATOR.userAgent,
        NAVIGATOR.language || '',
        c.getTimezoneOffset(),
        c.getFullYear() - 1900,
        c.getDate() + Math.floor(e / 24),
        (24 + e) % 24,
        (60 + c.getMinutes() + b) % 60,
      ].join('.')
    );
  };
  if (valueOnly) {
    return `2.${encodeURIComponent(parseTimeStamp(_gid + _ga, 0) + '.' + _gid + '-' + _ga)}`;
  }
  return `_ga=2.${encodeURIComponent(parseTimeStamp(_gid + _ga, 0) + '.' + _gid + '-' + _ga)}`;
};

const propagateStatsigUserId = (href: string) => {
  const userid = typeof window === 'undefined' ? null : getLocalStorage('statsigUserId');
  if (!href.includes('.mn.co') && !href.includes('/path_to_pro_offer')) {
    return '';
  }
  if (userid) {
    return `experimentVisitorId=${userid}`;
  }
  return '';
};

const propagateSegmentAnonymousId = () => {
  if (typeof window !== 'undefined') {
    const { ajs_anonymous_id: ajs_anonymous_id_cookie } = getCookies();
    // localstorage comes as a quoted string `"6ad26866-..."`
    const ajs_anonymous_id_storage = (localStorage.getItem('ajs_anonymous_id') || '').split('"').join('');
    const ajs_anonymous_id_script = (window as any).analytics?.user
      ? (window as any).analytics?.user()?.anonymousId()
      : '';
    // Fallback sequence ORs
    const ai = ajs_anonymous_id_script || ajs_anonymous_id_storage || ajs_anonymous_id_cookie || '';
    if (ai) {
      return `ai=${ai}`;
    }
    return '';
  }
};

const propagateRewardful = (href: string): string => {
  if (typeof window === 'undefined' || !href.includes('.mn.co')) {
    return '';
  }

  const { rewardfulViaId } = getCookies();

  if (rewardfulViaId) {
    return `via=${rewardfulViaId}`;
  } else {
    const queryParams = new URLSearchParams(window.location.search);

    if (queryParams.has('via')) {
      return `via=${queryParams.get('via')}`;
    }
  }

  return '';
};

/**
 * Get stored rewardful ID from query parameter or cookie
 * @returns string
 */
export const getRewardfulAffiliateId = (): string => {
  const queryParams = new URLSearchParams(window.location.search);

  if (queryParams.has('via')) {
    return queryParams.get('via');
  }

  const { rewardfulViaId } = getCookies();

  return rewardfulViaId ?? '';
};

export const getEverflowAffiliateId = (): string | null => {
  const queryParams = new URLSearchParams(window.location.search);
  return queryParams.get('affid') ?? null;
};

const propagateLocale = (href: string): string => {
  if (typeof window === 'undefined' || !href.includes('.mn.co')) {
    return '';
  }

  const language = getLanguageFromPath(window.location.pathname);

  if (language !== 'en') {
    return `locale=${language}`;
  }

  return '';
};

const propagateAllAvailable = (href: string): string => {
  if (typeof window === 'undefined' || !href.includes('.mn.co')) {
    return '';
  }

  const { queryStringParams } = getCookies();
  const resultString = [];

  if (queryStringParams) {
    const queryStringParamsParsed = JSON.parse(queryStringParams) as { [key: string]: string };

    for (const key in queryStringParamsParsed) {
      resultString.push(`${key}=${queryStringParamsParsed[key]}`);
    }
  } else {
    const queryParams = new URLSearchParams(window.location.search);

    queryParams.forEach((value, key) => {
      resultString.push(`${key}=${value}`);
    });
  }

  if (resultString.length) {
    return resultString.join('&');
  }

  return '';
};

export const propagateQueryToExternalLinks = (href: string): string => {
  if (urlIsExternal(href) && typeof window !== 'undefined') {
    href = href.replace('new.mn.co', 'cohost.mn.co');

    if (process.env.GATSBY_PRODUCT_ENDPOINT_REPLACE) {
      // This intends to replace original product page endpoint with another for testing purposes
      href = href.replace('cohost.mn.co', process.env.GATSBY_PRODUCT_ENDPOINT_REPLACE);
    }

    const queryPropagateRaw = [
      propagateUtmParams(),
      propagateGaParams(false),
      propagateStatsigUserId(href),
      propagateSegmentAnonymousId(),
      propagateRewardful(href),
      propagateAllAvailable(href),
      propagateEverflowParams(href),
      propagateLocale(href),
    ]
      .filter((s) => !!s)
      .join('&');

    const queryPropagate = [...new Set(queryPropagateRaw.split('&'))].join('&');

    if (!queryPropagate) {
      return href;
    } else if (`${href}${queryPropagate}`.includes('?')) {
      // >> possibly href includes a query already 'mn.co?type=me
      return `${href}&${queryPropagate}`;
    }
    return `${href}?${queryPropagate}`;
    // return href;
  } else {
    return href;
  }
};

/**
 * Normalize hostname by removing www. prefix for comparison.
 * This allows both www.mightynetworks.com and mightynetworks.com to be treated as the same domain.
 *
 * @param hostname - The hostname to normalize
 * @returns Hostname with www. prefix removed
 */
const normalizeHostname = (hostname: string): string => {
  return hostname.replace(/^www\./, '');
};

/**
 * Determines if a URL points to an external site.
 *
 * Treats both www.mightynetworks.com and mightynetworks.com as internal (apex domain support).
 * Subdomains (e.g., help.mightynetworks.com, blog.mightynetworks.com) are considered external.
 *
 * During SSR: Compares against GATSBY_SITE_URL env var or falls back to https://www.mightynetworks.com
 * On client: Compares against window.location.hostname
 *
 * @param url - Absolute or relative URL to check
 * @returns true if URL is external, false if internal or relative
 */
export const urlIsExternal = (url: string): boolean => {
  // Check if it's a relative URL (internal)
  if (!url.startsWith('http://') && !url.startsWith('https://') && !url.startsWith('//')) {
    return false;
  }

  // Handle protocol-relative URLs during SSR by prepending https:
  let normalizedUrl = url;
  if (url.startsWith('//') && typeof window === 'undefined') {
    normalizedUrl = 'https:' + url;
  }

  // If we have window, use the robust hostname comparison
  if (typeof window !== 'undefined') {
    const temporaryAnchor = document.createElement('a');
    temporaryAnchor.href = url;
    const normalizedUrlHostname = normalizeHostname(temporaryAnchor.hostname);
    const normalizedSiteHostname = normalizeHostname(window.location.hostname);
    return normalizedUrlHostname !== normalizedSiteHostname;
  }

  // During SSR, parse hostname and compare with site hostname
  const siteUrl = process.env.GATSBY_SITE_URL || 'https://www.mightynetworks.com';
  try {
    const urlHostname = new URL(normalizedUrl).hostname;
    const siteHostname = new URL(siteUrl).hostname;
    const normalizedUrlHostname = normalizeHostname(urlHostname);
    const normalizedSiteHostname = normalizeHostname(siteHostname);
    return normalizedUrlHostname !== normalizedSiteHostname;
  } catch {
    // If URL parsing fails, assume external for safety
    return true;
  }
};

// On click Link Track Propagation
// ===========================================

const reApplyUtmQueryToUrl = (urlBase: string): string => {
  const [url, query] = urlBase.split('?');
  const searchParams = new URLSearchParams(query);
  propagateUtmParams()
    .split('&')
    .filter((s) => !!s)
    .forEach((field) => {
      const [key, value] = field.split('=');
      if (searchParams.has(key)) {
        searchParams.delete(key);
      }
      searchParams.append(key, value);
    });
  if (searchParams.toString()) {
    return `${url}?${searchParams.toString()}`;
  } else {
    return url;
  }
};

const reApplyGaQueryToUrl = (urlBase: string): string => {
  const [url, query] = urlBase.split('?');
  const searchParams = new URLSearchParams(query);
  const ga = propagateGaParams(true);
  if (searchParams.has('_ga') && !!ga) {
    searchParams.delete('_ga');
    searchParams.append('_ga', ga);
  } else if (ga) {
    searchParams.append('_ga', ga);
  }
  if (searchParams.toString()) {
    return `${url}?${searchParams.toString()}`;
  } else {
    return url;
  }
};

const reApplyStatsigUserId = (urlBase: string): string => {
  const [url, query] = urlBase.split('?');
  const searchParams = new URLSearchParams(query);
  const statsigParam = propagateStatsigUserId(url);
  if (!statsigParam) {
    // Only applied to .mn.co url, if empty, no change
    return urlBase;
  }
  const [key, value] = statsigParam.split('=');
  if (searchParams.has(key)) {
    searchParams.delete(key);
  }
  searchParams.append(key, value);
  if (searchParams.toString()) {
    return `${url}?${searchParams.toString()}`;
  } else {
    return url;
  }
};

export const reApplyTrackingToUrl = (url: string): string => {
  url = reApplyUtmQueryToUrl(url);
  url = reApplyGaQueryToUrl(url);
  url = reApplyStatsigUserId(url);
  return url;
};

export const applyGanalyticsTracking =
  <T extends HTMLElement = HTMLAnchorElement>(queryPropagatedUrl: string, useSameTab = false) =>
  (event?: React.MouseEvent<T, MouseEvent>): void => {
    event?.preventDefault();
    if (urlIsExternal(queryPropagatedUrl)) {
      // External links to MN.CO,
      // >> Ga needs updating as it depends on timestamp
      const trackedUrl = reApplyTrackingToUrl(queryPropagatedUrl);
      if (!trackedUrl.includes('_ga')) {
        (window as any).TrackJS && (window as any).TrackJS.console.error('_ga missing!', { url: trackedUrl });
        (window as any).TrackJS && (window as any).TrackJS.track(`_ga missing! ${trackedUrl}`);
      }
      // Statsig event tracking
      // Track every call to action when leaving mightynetworks.com to cohost.mn.co
      if (
        queryPropagatedUrl.includes(`new.mn.co`) ||
        queryPropagatedUrl.includes(`cohost.mn.co`) ||
        queryPropagatedUrl.includes(process.env.GATSBY_PRODUCT_ENDPOINT_REPLACE)
      ) {
        logStatsigEvent('cta_clicked', 1);
      }
      // ------------------------
      // External links to other domains, some cases are opened in same tab
      if (useSameTab) {
        window.location.href = trackedUrl;
      } else {
        window.open(trackedUrl, '_blank').focus();
      }
    } else {
      if (queryPropagatedUrl.includes(`/request-a-call-form`)) {
        logStatsigEvent('request_a_call_clicked', 1);
      }

      // internal are defered to Gatsby (internals are faster)
      navigate(queryPropagatedUrl);
    }
  };

export const propagateEverflowParams = (href: string): string => {
  if (typeof window === 'undefined' || !href.includes('.mn.co')) {
    return '';
  }

  const cookieTransactionIds = getCookies()['ef_tid_c_a_1']?.split('|');

  if (href.includes('_ef_transaction_id') || !cookieTransactionIds) {
    return '';
  }

  return `_ef_transaction_id=${cookieTransactionIds[cookieTransactionIds.length - 1]}`;
};
